import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ActionBar, DataNeededEmptyCard } from 'common';
import useMediaQuery from '@mui/material/useMediaQuery';

import * as R from 'ramda';
import { useUserRolePermissions } from 'hooks';

import { bool } from 'prop-types';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/Error';
import { CircularProgress } from '@mui/material';
import { CreateReconsignmentContext, setup } from '../Create';
import Titlebar from '../Titlebar';
import Parameters from './Parameters';
import MainPanel from './MainPanel';

import { useCreateReconsignmentSetMutation } from '../../reconsignmentsEndpoints';
import GridPanel from './GridPanel';

const Setup = ({ bulk }) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const {
    filters,
    info,
    isDrawerOpen,
    setIsDrawerOpen,
    setOpenDialog,
    setIsNavHidden,
    bulkReconsignments,
    setBulkReconsignments,
  } = useContext(CreateReconsignmentContext);
  const { currentAffiliate } = useUserRolePermissions();

  const [sourceLocations, setSourceLocations] = useState(null);
  const [canContinueWithBulk, setCanContinueWithBulk] = useState(false);

  // Memoize the optional parameters to prevent unnecessary recalculations
  const optionalParams = useMemo(
    () => ({
      rvp: R.or(R.equals('Any', R.prop('rvp', info)), R.equals('', R.prop('rvp', info)))
        ? null
        : R.prop('rvp', info),
      volume: R.isEmpty(R.prop('volume', filters)) ? null : R.prop('volume', filters),
    }),
    [filters, info] // Include both entire objects as dependencies
  );

  // Memoize the filtered params
  const filtered = useMemo(() => R.reject(R.equals(null))(optionalParams), [optionalParams]);

  // Memoize the query params to prevent unnecessary API calls
  const params = useMemo(
    () => ({
      ...filtered,
      consignmentStatus: R.prop('consignmentStatus', filters),
      destinationIds: R.prop('isRecurring', info)
        ? R.path(['destinations', 'locationId'], info)
        : R.pluck('locationId', R.prop('destinations', info)),
      inventoryOwner: {
        companyCustomerIdName: currentAffiliate?.customerIdName,
        dataCompanyId: currentAffiliate?.dataCompanyId,
      },
      inventoryStatus: R.prop('inventoryStatus', filters),
      originIds: R.pluck('originId', R.prop('origins', filters)),
      productId: R.path(['product', 'productId'], info),
      productStatus: R.prop('productStatus', filters),
    }),
    [filtered, filters, info, currentAffiliate]
  );

  const [
    submit,
    {
      data: bulkData,
      error: bulkError,
      isError: isBulkError,
      isLoading: isBulkLoading,
      isSuccess: isBulkSuccess,
      reset: resetBulk,
    },
  ] = useCreateReconsignmentSetMutation();

  const resetRef = useRef(null);

  // Only update resetRef when resetBulk changes
  useEffect(() => {
    resetRef.current = resetBulk;
  }, [resetBulk]);

  // Memoize transformations to prevent unnecessary recalculations
  const transformations = useMemo(
    () => ({
      rvp: R.or(R.equals('Any', info.rvp), R.equals('', info.rvp))
        ? R.always(null)
        : R.always(info.rvp),
    }),
    [info.rvp]
  );

  // Memoize the getBody function to prevent unnecessary recalculations
  const getBody = useMemo(
    () =>
      R.compose(
        R.omit([
          'destinations',
          'errors',
          'isRecurring',
          'product',
          'sourceLocations',
          'origins',
          'overVol',
          'volume',
          'consignmentStatus',
          'inventoryStatus',
          'originIds',
          'productStatus',
          'toLocations',
          'fromLocations',
        ]),
        R.evolve(transformations)
      ),
    [transformations]
  );

  const stripInvalidReconsignments = r => {
    return Object.keys(r).filter(k => R.isNil(r[k])) < 1;
  };

  const handleBulkSubmit = () => {
    const body = {
      ...getBody(info),
      ...getBody(filters),
      inventoryOwner: {
        companyCustomerIdName: currentAffiliate.customerIdName,
        dataCompanyId: currentAffiliate.dataCompanyId,
      },
      originIds: params.originIds,
      productId: R.path(['product', 'productId'], info),
      reconsignments: R.filter(stripInvalidReconsignments, bulkReconsignments).map(r => {
        return {
          ...r,
          fromLocationId: r.fromLocationId.value,
          toLocationId: r.toLocationId.value,
        };
      }),
    };
    submit({ body });
  };

  const isBulkSubmitted = R.not(R.isNil(bulkData));

  // Memoize the change props to prevent unnecessary comparisons
  const changeProps = useMemo(
    () =>
      R.pick([
        'bulkReconsignments',
        'destinations',
        'isRecurring',
        'product',
        'transferDate',
        'rvp',
        'sourceLocations',
      ]),
    []
  );

  const contextProps = useMemo(() => changeProps(info), [changeProps, info]);
  const initialProps = useMemo(() => changeProps(setup), [changeProps]);
  const hasChanges = useMemo(
    () => !R.equals(initialProps, contextProps),
    [initialProps, contextProps]
  );

  const handleBack = event => {
    if (!bulk && hasChanges) {
      event.preventDefault();
      setOpenDialog(true);
    } else {
      setIsNavHidden(false);
    }
  };

  // Validations - memoize these to prevent unnecessary recalculations
  const sourceLocationSelected = R.prop('sourceLocations', info);
  const destinations = R.prop('destinations', info);

  // Memoize validation values to prevent unnecessary recalculations
  const isProductValid = useMemo(() => R.not(R.isNil(R.prop('product', info))), [info]);
  const isGasoline = useMemo(
    () => R.equals(R.T(), R.prop('isGasoline', R.prop('product', info))),
    [info]
  );

  const rvp = R.prop('rvp', info);

  const isRvpValid = useMemo(() => (isGasoline ? R.not(R.isNil(rvp)) : true), [isGasoline, rvp]);

  const isDestinationsValid = useMemo(() => R.not(R.isEmpty(sourceLocations)), [sourceLocations]);

  const isSourceLocationsValid = useMemo(
    () => R.not(R.isEmpty(sourceLocations)),
    [sourceLocations]
  );

  const checkValidDestinations = useMemo(
    () =>
      R.cond([
        [R.equals(0), R.always(false)],
        [R.equals(1), R.always(R.gte(R.length(sourceLocationSelected), 1))],
        [R.lt(1), R.always(R.equals(1, R.length(sourceLocationSelected)))],
      ]),
    [sourceLocationSelected]
  );

  const isValidMultipleDestinations = useMemo(
    () => checkValidDestinations(R.length(destinations)),
    [checkValidDestinations, destinations]
  );

  const bulkReconsignmentsNotEmpty = useMemo(
    () => R.not(R.isEmpty(bulkReconsignments)),
    [bulkReconsignments]
  );

  // Memoize validation arrays to prevent unnecessary recalculations
  const validations = useMemo(
    () => [
      isProductValid,
      isRvpValid,
      isDestinationsValid,
      isSourceLocationsValid,
      isValidMultipleDestinations,
    ],
    [
      isProductValid,
      isRvpValid,
      isDestinationsValid,
      isSourceLocationsValid,
      isValidMultipleDestinations,
    ]
  );

  const bulkValidations = useMemo(
    () => [isProductValid, isRvpValid, bulkReconsignmentsNotEmpty, canContinueWithBulk],
    [isProductValid, isRvpValid, bulkReconsignmentsNotEmpty, canContinueWithBulk]
  );

  return (
    <>
      <Titlebar
        bulk={bulk}
        progress={bulk ? null : 33.33}
        step={bulk ? null : 1}
        title={bulk ? 'Reconsignment: Bulk 1 to 1' : 'Create New Reconsignment'}
        totalSteps={bulk ? null : 3}
      />
      <Parameters
        bulk={bulk}
        isDrawerOpen={isDrawerOpen}
        isLoading={false}
        resetBulk={resetRef.current}
        results={sourceLocations}
        setBulkRows={setBulkReconsignments}
        setIsDrawerOpen={setIsDrawerOpen}
        setSourceLocations={setSourceLocations}
      />
      {bulk && isProductValid && isSourceLocationsValid ? (
        <Stack
          alignItems="flex-start"
          spacing={2}
          sx={{
            '& .MuiDataGrid-root': {
              minWidth: { lg: 'unset', xs: '800px' }, // Only use fixed min-width on mobile
            },
            // Add specific toolbar styling to fix overlap
            '& .MuiDataGrid-toolbarContainer': {
              maxWidth: '100%',
              overflow: 'hidden',
              position: 'static',
              width: '100%',
            },
            display: 'flex', // Change from 'block' to 'flex' for better Stack behavior
            flexDirection: 'column',
            // Create a proper stacking context
            isolation: 'isolate',

            marginLeft: { lg: '400px' },

            mt: 8,
            overflowX: 'hidden',
            // Change from 'auto' to 'hidden' to prevent horizontal overflow
            position: 'relative',

            pt: 5,

            textAlign: 'left',

            width: { lg: 'calc(100% - 400px)', xs: '100%' },
            zIndex: 1, // Lower z-index than drawer
          }}
        >
          {/* Keep the existing content */}
          {isBulkLoading && (
            <Box
              sx={{
                p: theme => ({ lg: theme.spacing(4, 1, 1), xs: theme.spacing(9, 3, 4) }),
                textAlign: 'center',
              }}
            >
              <CircularProgress size={48} />
            </Box>
          )}
          {isBulkSuccess && (
            <Box
              sx={{
                p: theme => ({ lg: theme.spacing(4, 1, 1), xs: theme.spacing(9, 3, 4) }),
                textAlign: 'center',
              }}
            >
              <CheckIcon color="secondary" sx={{ height: 35, width: 48 }} />
              <Typography color="text.primary" variant="h6">
                Your Reconsignments Were Successfully Submitted!
              </Typography>
              <Typography variant="body1">{bulkData.message}</Typography>
            </Box>
          )}
          {isBulkError && (
            <Box
              sx={{
                p: theme => ({ lg: theme.spacing(4, 1, 1), xs: theme.spacing(9, 3, 4) }),
                textAlign: 'center',
              }}
            >
              <ErrorIcon color="error" sx={{ height: 48, width: 48 }} />
              <Typography color="text.primary" variant="h6">
                Your Reconsignments could not be submitted.
              </Typography>
              <Typography variant="body1">{bulkError.data.title}</Typography>
            </Box>
          )}
          {!isBulkLoading && (
            <Box
              sx={{
                clipPath: 'inset(0)',

                // Add these properties to create containment
                maxWidth: '100%',

                overflow: 'hidden',
                p: 1,
                pb: 10,
                position: 'relative',
                width: '100%',
              }}
            >
              <GridPanel
                disableAction={isBulkSubmitted}
                rows={bulkReconsignments}
                setCanContinueWithBulk={setCanContinueWithBulk}
                setRows={setBulkReconsignments}
              />
            </Box>
          )}
        </Stack>
      ) : (
        <Box
          sx={{
            marginTop: 12,
            pb: 10,
          }}
        >
          {bulk ? (
            <DataNeededEmptyCard
              subtitle="Select a Product to begin adding reconsignments."
              title="Add 1 to 1 Reconsignments"
            />
          ) : (
            <MainPanel isFetching={false} sourceLocations={sourceLocations} />
          )}
        </Box>
      )}
      {R.or(lgUp, R.not(isDrawerOpen)) &&
        (bulk ? (
          <ActionBar
            backLabel={isBulkSubmitted ? 'Done' : 'Cancel'}
            backLink="../../"
            continueLabel={isBulkLoading ? 'Submitting...' : 'Submit'}
            disableAction={
              isBulkLoading || isBulkSubmitted || R.any(R.equals(false))(bulkValidations)
            }
            endIcon={
              isBulkLoading ? <CircularProgress size={20} sx={{ color: 'surface.white' }} /> : null
            }
            onContinue={() => !isBulkSubmitted && handleBulkSubmit()}
          />
        ) : (
          <ActionBar
            backLabel="Cancel"
            backLink={hasChanges ? '' : '../../'}
            continueLabel="Continue"
            continueLink="../allocation"
            disableAction={R.any(R.equals(false))(validations)}
            onBack={handleBack}
          />
        ))}
    </>
  );
};

Setup.propTypes = {
  bulk: bool,
};

Setup.defaultProps = {
  bulk: false,
};

export default Setup;
